import { Link } from 'react-router-dom';
import { Skeleton } from '@ah/design';
import { MediaThumb } from '@ah/design/mobile';
import type { MusicSong } from '../shared/types.ts';

export function formatDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SongRow({ song }: { song: MusicSong }) {
  const duration = formatDuration(song.variantADurationSec);
  return (
    <Link
      to={`/music/${song.id}`}
      className="flex gap-3 p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <MediaThumb src={song.imageUrl} className="w-14 h-14 rounded-lg shrink-0" iconSize={18} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-snug line-clamp-2">{song.title}</p>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
          {song.artist?.name ?? 'Unknown artist'}
          {duration ? <span> · {duration}</span> : null}
          {song.artist?.style ? <span> · {song.artist.style}</span> : null}
        </p>
      </div>
    </Link>
  );
}

export function SongListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex gap-3 p-3 bg-card rounded-xl border border-border">
          <Skeleton className="w-14 h-14 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ArtistsListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="p-3 bg-card rounded-xl border border-border space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

export function ErrorRetry({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">{label}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}

export const MUSIC_TABS = [
  { to: '/music', label: 'For You', end: true },
  { to: '/music/latest', label: 'Latest' },
  { to: '/music/artists', label: 'Artists' },
  { to: '/music/seen', label: 'Heard' },
  { to: '/music/saved', label: 'Saved' },
];
